import type { CoreRole } from '@/lib/zifekCore';
import { permissionLabel, roleMeta } from '@/lib/corePermissions';

interface Props {
  roles: CoreRole[];
}

export default function RolesPanel({ roles }: Props) {
  if (roles.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
        <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
          <i className="ri-shield-user-line text-2xl text-foreground-400"></i>
        </div>
        <p className="text-foreground-600">Aucun rôle.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {roles.map((r) => {
        const meta = roleMeta(r.role_key, r.name);
        const perms = r.permissions || [];
        return (
          <div key={r.id} className="bg-background-50 border border-background-200/70 rounded-lg p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.className}`}>
                  <i className={`${meta.icon} text-lg`}></i>
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-foreground-950 truncate">{r.name}</div>
                  <div className="text-[11px] text-foreground-400 truncate">niveau {r.level}</div>
                </div>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${r.is_system ? 'bg-background-100 text-foreground-600' : 'bg-primary-100 text-primary-900'}`}>
                {r.is_system ? 'Système' : 'Personnalisé'}
              </span>
            </div>
            {r.description && <p className="text-xs text-foreground-500 mb-3 leading-relaxed">{r.description}</p>}
            <div className="flex flex-wrap gap-1.5 pt-3 border-t border-background-200/60">
              {perms.includes('*') ? (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary-100 text-primary-900 font-medium">Toutes les permissions</span>
              ) : perms.length === 0 ? (
                <span className="text-[10px] text-foreground-400">Aucune permission</span>
              ) : (
                perms.slice(0, 12).map((p) => (
                  <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 font-medium whitespace-nowrap">
                    {permissionLabel(p)}
                  </span>
                ))
              )}
              {perms.length > 12 && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 font-medium">+{perms.length - 12}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}